import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_URL !== undefined
    ? process.env.REACT_APP_API_URL
    : "http://localhost:8000/api";

const api = axios.create({
  baseURL: API_BASE_URL,
});

// ─── Item Types ──────────────────────────────────────────────

export type ItemStatus = "purchased" | "priced" | "in_booth" | "sold";

export interface StatusChange {
  id: number;
  item: number;
  from_status: string | null;
  to_status: string;
  changed_at: string;
  note: string;
}

export interface Item {
  id: number;
  description: string;
  photo: string | null;
  purchase_price: string;
  sale_price: string | null;
  status: ItemStatus;
  status_display: string;
  status_changes?: StatusChange[];
  created_at: string;
  updated_at: string;
}

export interface AvailableMonth {
  year: number;
  month: number;
}

export interface DashboardData {
  month: number;
  year: number;
  available_months: AvailableMonth[];
  total_items: number;
  purchased_count: number;
  priced_count: number;
  in_booth_count: number;
  sold_count: number;
  total_invested: string;
  total_revenue: string;
  gross_profit: string;
  credit_card_fees: string;
  booth_commission: string;
  booth_fee: string;
  rent_overhead: string;
  net_profit: string;
  tax_rate: string;
}

export interface AppSettings {
  id: number;
  tax_rate: string;
  credit_card_fee_rate: string;
  booth_commission_rate: string;
  booth_fee: string;
  rent_overhead: string;
  updated_at: string;
}

// ─── Items API ───────────────────────────────────────────────

export const itemsApi = {
  list: (params?: { status?: string; search?: string; ordering?: string }) =>
    api.get<Item[]>("/items/", { params }),

  get: (id: number) => api.get<Item>(`/items/${id}/`),

  create: (data: FormData) =>
    api.post<Item>("/items/", data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  update: (id: number, data: FormData | Record<string, unknown>) => {
    const isFormData = data instanceof FormData;
    return api.patch<Item>(`/items/${id}/`, data, {
      headers: isFormData
        ? { "Content-Type": "multipart/form-data" }
        : undefined,
    });
  },

  delete: (id: number) => api.delete(`/items/${id}/`),

  statusChanges: (id: number) =>
    api.get<StatusChange[]>(`/items/${id}/status-changes/`),

  bulkUpdateStatus: (itemIds: number[], status: ItemStatus) =>
    api.post<{ updated: number; total: number }>("/items/bulk-status/", {
      item_ids: itemIds,
      status,
    }),

  bulkDelete: (itemIds: number[]) =>
    api.post<{ deleted: number }>("/items/bulk-delete/", {
      item_ids: itemIds,
    }),

  exportCsv: (params?: { status?: string; search?: string }) =>
    api.get("/items/export/", { params, responseType: "blob" }),
};

// ─── Dashboard API ───────────────────────────────────────────

export const dashboardApi = {
  get: (params?: { month?: number; year?: number }) =>
    api.get<DashboardData>("/items/dashboard/", { params }),
};

// ─── Settings API ────────────────────────────────────────────

export const settingsApi = {
  get: () => api.get<AppSettings>("/settings/"),
  update: (data: Partial<AppSettings>) =>
    api.patch<AppSettings>("/settings/", data),
};

export default api;
